import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Shirt, LogOut, LayoutDashboard, Menu, X, Bell } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/notifications`, { withCredentials: true });
      setUnreadCount(r.data.unread_count || 0);
      setNotifications(r.data.notifications || []);
    } catch { }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(() => {
      // Only poll when tab is visible
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { }
  };

  const handleLogout = async () => {
    navigate('/');
    await logout();
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const dashboardLink = user?.role === 'startup' ? '/dashboard/startup' : user?.role === 'admin' ? '/admin' : '/my-tshirt';
  const isApprovedUser = !user || user.role === 'startup' || user.role === 'admin' || user.approval_status === 'approved';

  return (
    <nav className="sticky top-0 z-50 border-b-[3px] border-[#121212]" style={{ backgroundColor: '#F6F1E8' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 md:gap-3 group min-w-0" data-testid="nav-logo">
            <Shirt className="w-5 h-5 md:w-7 md:h-7 flex-shrink-0 group-hover:rotate-12 transition-transform" strokeWidth={3} />
            <span className="text-sm md:text-xl font-black uppercase tracking-tight whitespace-nowrap" style={{ fontFamily: 'Outfit, sans-serif', wordSpacing: '0.25em' }}>
              The SurfaceArea Project
            </span>
          </Link>

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                {isApprovedUser && (
                  <Link to={dashboardLink} className="text-sm font-bold uppercase tracking-wider hover:text-[#FF6B6B] transition-colors" data-testid="nav-dashboard">
                    <span className="flex items-center gap-1"><LayoutDashboard className="w-4 h-4" strokeWidth={2.5} /> {user?.role === 'startup' ? 'Dashboard' : user?.role === 'admin' ? 'Admin' : 'My T-Shirt'}</span>
                  </Link>
                )}

                {/* Notification Bell */}
                {isApprovedUser && (
                  <div className="relative">
                    <button
                      data-testid="notification-bell"
                      onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs && unreadCount > 0) handleMarkRead(); }}
                      className="relative hover:text-[#FF6B6B] transition-colors"
                    >
                      <Bell className="w-5 h-5" strokeWidth={2.5} />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 bg-[#FF6B6B] text-white text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center border-[1.5px] border-[#121212]" data-testid="notif-badge">
                          {unreadCount}
                        </span>
                      )}
                    </button>

                    {showNotifs && (
                      <div className="absolute right-0 top-10 w-80 bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-xl overflow-hidden z-50" data-testid="notif-dropdown">
                        <div className="px-4 py-3 border-b-[2px] border-[#121212] font-black text-xs uppercase tracking-wider">
                          Notifications
                        </div>
                        {notifications.length === 0 ? (
                          <div className="px-4 py-6 text-center text-sm font-medium" style={{ color: '#888' }}>
                            No notifications yet
                          </div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto">
                            {notifications.map(n => (
                              <div
                                key={n.notification_id}
                                className={`px-4 py-3 border-b border-[#121212]/10 last:border-b-0 ${!n.read ? 'bg-[#E8C86A]/15' : ''}`}
                              >
                                <div className="font-bold text-xs mb-0.5">{n.title}</div>
                                <div className="text-xs font-medium" style={{ color: '#666' }}>{n.message}</div>
                                <div className="text-[10px] font-bold mt-1" style={{ color: '#aaa' }}>
                                  {getTimeAgo(n.created_at)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {notifications.length > 0 && (
                          <Link
                            to="/my-tshirt"
                            className="block px-4 py-2.5 text-center text-xs font-bold uppercase border-t-[2px] border-[#121212] hover:text-[#FF6B6B]"
                            onClick={() => setShowNotifs(false)}
                          >
                            View Dashboard
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-3 ml-2 pl-4 border-l-[2px] border-[#121212]">
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#888' }}>{user.name?.split(' ')[0]}</span>
                  <button data-testid="logout-btn" onClick={handleLogout} className="text-sm hover:text-[#FF6B6B] transition-colors">
                    <LogOut className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>
              </>
            ) : (
              <button
                data-testid="nav-login-btn"
                onClick={handleLogin}
                className="bg-[#FF6B6B] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] hover:-translate-x-[1px] hover:shadow-[4px_4px_0px_0px_#121212] active:translate-y-[3px] active:translate-x-[3px] active:shadow-none transition-all rounded-lg px-4 py-1.5"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile: Sign In CTA + menu toggle */}
          <div className="md:hidden flex items-center gap-2">
            {!user ? (
              <button
                data-testid="mobile-signin-btn"
                onClick={handleLogin}
                className="bg-[#FF6B6B] text-[#121212] font-black uppercase text-xs border-[3px] border-[#121212] shadow-[2px_2px_0px_0px_#121212] hover:-translate-y-[1px] hover:shadow-[3px_3px_0px_0px_#121212] active:translate-y-[2px] active:shadow-none transition-all rounded-lg px-3 py-1.5"
              >
                Sign In
              </button>
            ) : (
              <button onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
                {menuOpen ? <X className="w-6 h-6" strokeWidth={3} /> : <Menu className="w-6 h-6" strokeWidth={3} />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t-[2px] border-[#121212] p-4 space-y-3" style={{ backgroundColor: '#EDE3C7' }}>

          {user ? (
            <>
              {isApprovedUser && <Link to={dashboardLink} className="block text-sm font-bold uppercase" onClick={() => setMenuOpen(false)}>Dashboard</Link>}
              {isApprovedUser && unreadCount > 0 && (
                <Link to="/my-tshirt" className="block text-sm font-bold uppercase text-[#FF6B6B]" onClick={() => setMenuOpen(false)}>
                  {unreadCount} new notification{unreadCount !== 1 ? 's' : ''}
                </Link>
              )}
              <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="block text-sm font-bold uppercase text-[#FF6B6B]">Logout</button>
            </>
          ) : (
            <button onClick={() => { handleLogin(); setMenuOpen(false); }} className="block text-sm font-bold uppercase text-[#FF6B6B]">Sign In</button>
          )}
        </div>
      )}

      {/* Backdrop to close notifs */}
      {showNotifs && <div className="fixed inset-0 z-40" onClick={() => setShowNotifs(false)} />}
    </nav>
  );
}

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
